import React from 'react';
import { Typography, Paper, Box } from '@mui/material';
import './PriceTable.css';
import { DataGrid, GridActionsCellItem, GridColDef, GridRowId, GridToolbarQuickFilter } from '@mui/x-data-grid';
import { Price } from '../../services/data/PricingV2Interfaces';
import {
  BillingLeverDetails,
  FilterOption,
  PricingCatalogByLeverId,
} from '../../services/data/PricingCatalogInterfaces';
import { PricingCatalogService } from '../../services/PricingCatalogService';
import { FeeCurrency } from '../../CommonInterfaces';
import { getCurrencySymbol } from '../../lib/currencyHelpers';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

export interface PriceTableProps {
  title: string;
  prices: Price[];
  pricingCatalog: PricingCatalogByLeverId;
  filterOptions: FilterOption[];
  loading: boolean;
  disabled: boolean;
  feeCurrency?: FeeCurrency;
  onEditPrice(leverId: number, suffix: string, uom: string): void;
  onDeletePrice(leverId: number, suffix: string, uom: string): void;
}

// Price but with mappings and transformations for convenience.
interface priceGridRow extends Price {
  id: number;
  fullRateDescription: string;
  filterValuesJoined: string;
}

// Renders a data grid displaying the prices for a given category of prices.
const PriceTable: React.FC<PriceTableProps> = ({
  title,
  prices,
  pricingCatalog,
  filterOptions,
  loading,
  disabled,
  feeCurrency,
  onEditPrice,
  onDeletePrice,
}) => {
  const symbol = feeCurrency ? getCurrencySymbol(feeCurrency) : '$';
  const filterOptionsByLeverId = React.useMemo(
    () => PricingCatalogService.groupFiltersByLeverId(filterOptions),
    [filterOptions],
  );

  const rows: priceGridRow[] = React.useMemo(() => {
    return createPriceGridRows(prices);
  }, [prices, pricingCatalog, filterOptionsByLeverId, loading]);

  const columns: GridColDef[] = [
    {
      field: 'fullRateDescription',
      headerName: 'Rate Description',
      flex: 1.5,
      minWidth: 200,
    },
    {
      field: 'unitOfMeasure',
      headerName: 'UOM',
      flex: 1,
      minWidth: 100,
    },
    {
      field: 'shipperFee',
      headerName: 'SH Price',
      type: 'number',
      flex: 1,
      minWidth: 120,
      align: 'left',
      headerAlign: 'left',
      valueFormatter: ({ value }) => `${symbol}${Number(value).toFixed(2)}`,
    },
    {
      field: 'warehouseFee',
      headerName: 'WH Price',
      type: 'number',
      flex: 1,
      minWidth: 120,
      align: 'left',
      headerAlign: 'left',
      valueFormatter: ({ value }) => `${symbol}${Number(value).toFixed(2)}`,
    },
    {
      field: 'filterValuesJoined',
      headerName: 'Filters',
      flex: 2,
      minWidth: 150,
      cellClassName: 'filter-values-cell',
    },
    {
      field: 'actions',
      type: 'actions',
      width: 80,
      cellClassName: 'actions',
      getActions: ({ id }) => {
        if (disabled) {
          return [];
        }

        return [
          <GridActionsCellItem
            name="delete"
            icon={<DeleteIcon />}
            role="button"
            label="Delete"
            onClick={handleDeleteClick(id)}
            color="inherit"
          />,
          <GridActionsCellItem
            name="edit"
            icon={<EditIcon />}
            role="button"
            label="Edit"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
        ];
      },
    },
  ];

  const handleDeleteClick = (id: GridRowId) => () => {
    const deletedRow = rows.find((row) => row.id === id);
    onDeletePrice(deletedRow.pricingLeverId, deletedRow.rateDescriptionSuffix, deletedRow.unitOfMeasure);
  };

  const handleEditClick = (id: GridRowId) => () => {
    const editedRow = rows.find((row) => row.id === id);
    onEditPrice(editedRow.pricingLeverId, editedRow.rateDescriptionSuffix, editedRow.unitOfMeasure);
  };

  if (prices.length === 0) {
    return (
      <Paper className="price-table-empty">
        <Typography variant="subtitle1">No pricing setup.</Typography>
      </Paper>
    );
  } else {
    return (
      <DataGrid
        rows={rows}
        columns={columns}
        slots={{ toolbar: QuickSearchToolbar }}
        autoHeight={true}
        loading={loading}
        pageSizeOptions={[5, 10, 25]}
        disableVirtualization
        getRowHeight={() => 'auto'}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 5 },
          },
        }}
        className="price-table-grid"
      />
    );
  }

  function fullRateDescription(price: Price): string {
    const leverDetails: BillingLeverDetails = pricingCatalog[price.pricingLeverId];
    if (price.rateDescriptionSuffix) {
      return `${leverDetails.display_name} - ${price.rateDescriptionSuffix}`;
    }
    return leverDetails.display_name;
  }

  function createPriceGridRows(inputPrices: Price[]): priceGridRow[] {
    return inputPrices.map((price, index) => ({
      id: index, // DataGrid requires an 'id' field.
      fullRateDescription: fullRateDescription(price),
      filterValuesJoined: filterValuesString(price),
      ...price,
    }));
  }

  function filterValuesString(price: Price): string {
    if (loading) {
      return '';
    }
    return price.priceFilterValues
      .map((filterValue) => {
        const filterOption: FilterOption = filterOptionsByLeverId[price.pricingLeverId].find((item) => {
          return item.pricing_lever_filter_id === filterValue.pricingLeverFilterId;
        });
        return `${filterOption.display_name}: ${filterValue.value}`;
      })
      .join('\n\n');
  }

  function QuickSearchToolbar() {
    return (
      <Box className="quick-search-toolbar">
        <Typography variant="h6" component="h3" className="quick-search-toolbar-title">
          {title}
        </Typography>
        <GridToolbarQuickFilter />
      </Box>
    );
  }
};

export default PriceTable;
